import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ConsentService } from '../../../services/consent.service';

/**
 * Cookie/analytics consent banner — the CMP the ConsentService was waiting
 * for. Shows ONLY while no choice has ever been stored; both buttons persist
 * the choice so it never reappears. Accept flips ConsentService, which
 * initializes PostHog and flushes the queued events; reject keeps analytics
 * permanently off (still changeable later from the privacy page).
 */
@Component({
  selector: 'app-cookie-consent',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  template: `
    @if (visible()) {
      <div class="ccb" role="dialog" aria-live="polite" [attr.aria-label]="'cookieConsent.title' | translate">
        <div class="ccb__body">
          <p class="ccb__title">{{ 'cookieConsent.title' | translate }}</p>
          <p class="ccb__text">
            {{ 'cookieConsent.body' | translate }}
            <a routerLink="/privacy" class="ccb__link">{{ 'cookieConsent.learnMore' | translate }}</a>
          </p>
        </div>
        <div class="ccb__actions">
          <button type="button" class="ap-btn ap-btn--plain ap-btn--sm" (click)="decide(false)">
            {{ 'cookieConsent.reject' | translate }}
          </button>
          <button type="button" class="ap-btn ap-btn--filled ap-btn--sm" (click)="decide(true)">
            {{ 'cookieConsent.accept' | translate }}
          </button>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .ccb {
        position: fixed;
        left: 16px;
        right: 16px;
        bottom: 16px;
        z-index: var(--ap-z-consent);
        margin: 0 auto;
        max-width: 640px;
        display: flex;
        align-items: center;
        gap: 16px;
        background: #1d1d1f;
        color: #f5f5f7;
        border-radius: 16px;
        padding: 14px 18px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
      }
      .ccb__body {
        flex: 1;
        min-width: 0;
      }
      .ccb__title {
        margin: 0;
        font-size: 14px;
        font-weight: 650;
      }
      .ccb__text {
        margin: 4px 0 0;
        font-size: 13px;
        line-height: 1.45;
        color: rgba(245, 245, 247, 0.75);
      }
      .ccb__link {
        color: #6fd598;
        text-decoration: none;
      }
      .ccb__link:hover {
        text-decoration: underline;
      }
      .ccb__actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
      }
      .ccb__actions .ap-btn--plain {
        color: rgba(245, 245, 247, 0.85);
      }
      @media (max-width: 560px) {
        .ccb {
          flex-direction: column;
          align-items: stretch;
          text-align: left;
        }
        .ccb__actions {
          justify-content: flex-end;
        }
      }
    `,
  ],
})
export class CookieConsentComponent {
  private readonly consent = inject(ConsentService);

  readonly visible = signal(!this.consent.hasStoredChoice);

  decide(granted: boolean): void {
    this.consent.setAnalyticsConsent(granted);
    this.visible.set(false);
  }
}
